import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';

import { isWebPushNotificationSupported } from 'utils/browsers';
import logger from 'utils/logging/faro';

import { disableWebPushNotifications, enableWebPushNotifications } from './webPushNotifications';

interface WebPushContextType {
  isPushSupported: boolean;
  pushPermission: NotificationPermission | null;
  isPushEnabled: boolean;
  enablePush: (mxAccessToken: string) => Promise<void>;
  disablePush: (mxAccessToken: string) => Promise<void>;
}

const WebPushContext = createContext<WebPushContextType | undefined>(undefined);

export const WebPushProvider = ({ children }: PropsWithChildren) => {
  const [isPushSupported, setIsPushSupported] = useState(isWebPushNotificationSupported);
  const [pushPermission, setPushPermission] = useState<NotificationPermission | null>(null);
  const [isPushEnabled, setIsPushEnabled] = useState(false);

  useEffect(() => {
    if (!isPushSupported) return;

    navigator.serviceWorker.register('/web-push-sw.js').catch((err) => {
      logger.error(err);
      setIsPushSupported(false);
    });
  }, [isPushSupported]);

  useEffect(() => {
    if (!isPushSupported) return;
    const icon = window.__APP_CONFIG__?.faviconSrc ?? '';
    navigator.serviceWorker.ready.then((registration) => {
      registration.active?.postMessage({ type: 'THEME_CONFIG', icon });
    });
  }, [isPushSupported]);

  useEffect(() => {
    if (!isPushSupported) return;

    setPushPermission(Notification.permission);

    if (Notification.permission !== 'granted') return;

    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => setIsPushEnabled(subscription !== null))
      .catch(logger.error);
  }, [isPushSupported]);

  const enablePush = useCallback(async (mxAccessToken: string) => {
    const result = await enableWebPushNotifications(mxAccessToken);

    if (result === 'granted') {
      setPushPermission('granted');
      setIsPushEnabled(true);
    } else if (result === 'denied') {
      setPushPermission('denied');
      setIsPushEnabled(false);
    }
  }, []);

  const disablePush = useCallback(async (mxAccessToken: string) => {
    const result = await disableWebPushNotifications(mxAccessToken);

    if (result === 'ok') setIsPushEnabled(false);
  }, []);

  return (
    <WebPushContext.Provider
      value={{
        isPushSupported,
        pushPermission,
        isPushEnabled,
        enablePush,
        disablePush,
      }}
    >
      {children}
    </WebPushContext.Provider>
  );
};

export const useWebPushContext = (): WebPushContextType => {
  const context = useContext(WebPushContext);

  if (context === undefined) throw new Error('useWebPushContext must be used within WebPushProvider');

  return context;
};
